"use client";

import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowUpRight,
  Clock,
  MessageSquare,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import Link from "next/link";
import { SupportTicketForm } from "@/app/support/components/SupportTicketForm";
import { cn } from "@/lib/utils";
import { QUICK_LINKS, SUPPORT_TYPES } from "../../constants";
import type { SupportTone } from "../supportTheme";

const EASE = [0.16, 1, 0.3, 1] as const;

const CATEGORY_ICONS = [Wrench, MessageSquare, ShieldCheck] as const;

interface SupportTicketSectionProps {
  tone: SupportTone;
  isDark: boolean;
}

export default function SupportTicketSection({
  tone,
  isDark,
}: SupportTicketSectionProps) {
  const reduceMotion = useReducedMotion();

  const reveal = (delay = 0) =>
    reduceMotion
      ? { initial: false as const }
      : {
          initial: { opacity: 0, y: 22, filter: "blur(10px)" },
          whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
          viewport: { once: true, margin: "-12% 0px -8% 0px" },
          transition: { duration: 0.72, delay, ease: EASE },
        };

  const intro = reduceMotion
    ? { initial: false as const }
    : {
        initial: { opacity: 0, y: 18, filter: "blur(10px)" },
        animate: { opacity: 1, y: 0, filter: "blur(0px)" },
        transition: { duration: 0.72, ease: EASE },
      };

  return (
    <section className="px-5 py-8 sm:px-8 sm:py-10">
      <div className="mx-auto grid max-w-[1280px] gap-8 lg:grid-cols-[minmax(0,0.82fr)_minmax(430px,1fr)] lg:items-start lg:gap-12">
        <motion.div {...intro} className="space-y-5 lg:space-y-4 xl:space-y-5">
          <div>
            <p
              className={cn(
                "text-[11px] font-medium tracking-[0.12em] uppercase",
                tone.kicker,
              )}
            >
              Zom support
            </p>
            <h1
              className={cn(
                "mt-3 max-w-[520px] text-[34px] leading-[1.08] font-semibold tracking-[-0.025em] sm:text-[40px]",
                tone.heading,
              )}
            >
              Get help from the product team
            </h1>
            <p
              className={cn(
                "mt-4 max-w-[540px] text-[15px] leading-6",
                tone.body,
              )}
            >
              Describe what you were doing, what you expected, and what
              happened. A product specialist will review the request and reply
              by email, usually within one business day.
            </p>
          </div>

          <motion.div
            {...reveal(0.05)}
            className="space-y-4 lg:space-y-3 xl:space-y-4"
          >
            <div
              className={cn(
                "grid gap-px overflow-hidden rounded-[12px] border",
                tone.gridShell,
              )}
            >
              {SUPPORT_TYPES.map((type, index) => {
                const Icon = CATEGORY_ICONS[index] ?? Wrench;
                return (
                  <article
                    key={type.title}
                    className={cn(
                      "flex items-start gap-3.5 p-3 lg:p-3 xl:p-3.5",
                      tone.card,
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px]",
                        tone.iconChip,
                      )}
                    >
                      <Icon
                        className="h-[16px] w-[16px]"
                        strokeWidth={1.6}
                        aria-hidden
                      />
                    </span>
                    <div className="min-w-0">
                      <h3
                        className={cn(
                          "font-display text-[14px] font-medium tracking-tight",
                          tone.cardTitle,
                        )}
                      >
                        {type.title}
                      </h3>
                      <p
                        className={cn(
                          "mt-1 text-[12.5px] leading-[18px] xl:leading-5",
                          tone.cardBody,
                        )}
                      >
                        {type.description}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>

            <div
              className={cn("rounded-[12px] border p-3 xl:p-3.5", tone.callout)}
            >
              <div className="flex items-start gap-3">
                <Clock
                  className={cn("mt-0.5 h-4 w-4 shrink-0", tone.accent)}
                  aria-hidden
                />
                <div className="space-y-1.5">
                  <p className="text-[12.5px] leading-[18px] xl:text-[13px] xl:leading-5">
                    <span className={cn("font-medium", tone.calloutStrong)}>
                      What happens next.{" "}
                    </span>
                    <span className={tone.calloutBody}>
                      Every request is reviewed and answered over email.
                      You&apos;ll get a confirmation, then a detailed follow-up
                      from the team.
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <div>
              <p
                className={cn(
                  "font-mono text-[10px] font-medium tracking-[0.2em] uppercase",
                  tone.faint,
                )}
              >
                Helpful links
              </p>
              <div className="mt-2.5 grid gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap">
                {QUICK_LINKS.map((link) => (
                  <Link
                    key={link.title}
                    href={link.href}
                    className={cn(
                      "group font-ui flex items-center justify-between rounded-[10px] border px-3.5 py-1.5 text-[12.5px] font-medium transition-colors lg:px-2.5 lg:py-1 xl:px-3.5 xl:py-1.5",
                      tone.quickLink,
                    )}
                  >
                    {link.title}
                    <ArrowUpRight className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div {...reveal(0.12)} id="report" className="scroll-mt-28">
          <SupportTicketForm tone={tone} isDark={isDark} />
        </motion.div>
      </div>
    </section>
  );
}
